import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TabConfiguration from '../tabs/TabConfiguration';
import DangerZoneRGPD from '../components/DangerZoneRGPD/DangerZoneRGPD';
import { apiGet, apiPut } from '../utils/api-client';

/**
 * TabConfigurationContainer
 *
 * Endpoints branchés :
 *  - GET  /api/restaurants/config  → données existantes (préremplir le wizard)
 *  - PUT  /api/restaurants/config  → sauvegarde du wizard (au "Terminer")
 *  - GET  /api/dashboard/          → fallback pour récupérer le restaurant
 */
export default function TabConfigurationContainer() {
  const { t } = useTranslation('tabs');
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);

  /** Récupère la config actuelle pour pré-remplir le wizard. */
  useEffect(() => {
    (async () => {
      try {
        // /api/restaurants/config peut retourner les vrais champs DB
        const cfg = await apiGet('/api/restaurants/config').catch(() => null);
        const dash = await apiGet('/api/dashboard/').catch(() => null);
        const r = cfg || dash?.restaurant || {};
        setInitialData({
          restoName: r.nom || '',
          type:      r.type_cuisine || '',
          address:   r.adresse || '',
          ville:     r.ville || '',
          whatsapp:  r.whatsapp_number || r.whatsapp_message || '',
          phone:     r.telephone || '',
          website:   r.site_web || r.website || '',
          placeId:   r.google_place_id || '',
          googleUrl: r.google_maps_url || '',
          ticket:    r.ticket_moyen || '',
          couverts:  r.couverts_semaine ? Math.round(r.couverts_semaine / 7) : '',
          cible:     r.note_cible || 4.5,
          objectifAvis: r.objectif_avis_mois || 30,
          // Sprint 1 — ticket 2 : `objectif_horizon_mois` est désormais persisté
          // en DB (colonne dédiée). On lit la valeur (nombre 1..36) et on la
          // reformate en string 'Xm' attendue par l'UI du wizard. Compatibilité
          // ascendante : si null/undefined (resto pré-migration), fallback '6m'.
          horizonObjectif: r.objectif_horizon_mois
            ? `${r.objectif_horizon_mois}m`
            : '6m',
          googleSync: r.note_actuelle ? {
            note: r.note_actuelle,
            avis: r.nb_avis_total,
            rps:  r.rps_score,
            rpsSub: r.rps_score >= 80
            ? t('configuration.rps_excellence')
            : r.rps_score >= 60
              ? t('configuration.rps_bon_niveau')
              : t('configuration.rps_a_renforcer'),
          } : null,
        });
      } catch (e) {
        console.error('[TabConfiguration] init load error:', e);
        setInitialData({});
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  /**
   * Sauvegarde la config en fin de wizard.
   *
   * Backend CONFIG_FIELDS_SCHEMA (whitelist stricte, voir routes/restaurants.js) :
   *   nom, ville, type_cuisine, adresse, telephone, site_web,
   *   google_maps_url, google_place_id, ticket_moyen, couverts_semaine,
   *   clients_par_jour, note_depart, avis_depart, whatsapp_message,
   *   delai_envoi_minutes, objectif_avis
   *
   * `note_cible` reste ignoré côté backend (pas de colonne en DB).
   *
   * Sprint 1 — ticket 2 (mai 2026) — La sémantique est désormais SÉPARÉE :
   *  - `data.horizonObjectif` ('3m'/'6m'/'12m') → DB `objectif_horizon_mois` (3/6/12)
   *  - `delai_envoi_minutes` n'est PLUS touché par le wizard (config SMS dédiée
   *    dans l'onglet Collecte, Sprint 1 ticket 2 même PR).
   *
   * Historique : le hotfix PR #74 avait retiré `delai_envoi_minutes` du payload
   * pour stopper la corruption (le wizard envoyait '6m' interprété comme nombre
   * de minutes, fallback 60 → tous les restos avaient delai_envoi_minutes = 60).
   * Ce ticket finit le travail en ajoutant la persistance propre de l'horizon.
   */
  const handleComplete = useCallback(async (data) => {
    try {
      const payload = {
        nom:                  data.restoName,
        ville:                data.ville,
        type_cuisine:         data.type,
        adresse:              data.address,
        telephone:            data.phone,
        site_web:             data.website,
        google_place_id:      data.placeId,
        google_maps_url:      data.googleUrl,
        ticket_moyen:         data.ticket ? parseFloat(data.ticket) : null,
        couverts_semaine:     data.couverts ? parseInt(data.couverts) * 7 : null,
        whatsapp_message:     data.whatsapp,
        objectif_avis:        data.objectifAvis ? parseInt(data.objectifAvis) : null,
        // delai_envoi_minutes : volontairement ABSENT du payload du wizard.
        // Ce champ a sa propre UI dédiée dans l'onglet Collecte (encart "Délai
        // d'envoi SMS"). Sémantiques séparées depuis Sprint 1 ticket 2.
        // Sprint 1 — ticket 2 : horizon objectif persisté.
        // 'Xm' → X (parseInt tolère le suffixe m, et accepte aussi un nombre brut).
        // Validation backend : entier 1..36 (rejet si hors range → 400).
        objectif_horizon_mois: data.horizonObjectif
          ? parseInt(String(data.horizonObjectif).replace(/m$/, ''), 10) || null
          : null,
      };
      // Champs string optionnels : '' doit être envoyé comme null (= vider la
      // colonne en DB). Pour les autres champs (requis ou numériques), on
      // continue à filtrer null/'' /undefined pour ne pas écraser bêtement.
      const STRING_OPTIONAL_FIELDS = [
        'type_cuisine', 'adresse', 'telephone', 'site_web',
        'whatsapp_message', 'google_place_id', 'google_maps_url',
      ];
      const filtered = Object.fromEntries(
        Object.entries(payload)
          .filter(([k, v]) => {
            if (STRING_OPTIONAL_FIELDS.includes(k)) return v !== undefined;
            return v !== null && v !== '' && v !== undefined;
          })
          .map(([k, v]) => [
            k,
            STRING_OPTIONAL_FIELDS.includes(k) && v === '' ? null : v,
          ])
      );

      const result = await apiPut('/api/restaurants/config', filtered);
      console.log('[Config] save OK:', result);

      const fieldCount = Object.keys(filtered).length;
      alert(t('configuration.alerts.save_success', {
        count: fieldCount,
        fields: Object.keys(filtered).join(', '),
      }));
      window.location.href = '/dashboard';
    } catch (e) {
      console.error('[Config] save error:', e);
      alert(t('configuration.alerts.save_failed', { error: e.message }));
    }
  }, [t]);

  /** Valide un Place ID en cherchant la fiche Google. */
  const handleValidatePlaceId = useCallback(async (placeId) => {
    try {
      // On interroge Place Details côté backend : il renvoie `found: true` si
      // Google a bien résolu le place_id, `found: false` sinon (404 / format
      // invalide / erreur API).
      // ⚠️ On n'utilise PLUS /api/places/competitors ici : depuis le palier
      // Découverte, cet endpoint est réservé à la capacité `competitors` et
      // renverrait un 403 — alors que la Configuration reste ouverte à tous
      // les plans.
      const r = await apiGet(`/api/restaurants/place-details?placeId=${encodeURIComponent(placeId)}`);
      return r?.found === true;
    } catch (e) {
      return false;
    }
  }, []);

  /**
   * Récupère les coordonnées (adresse, tel, site web) d'un Place ID Google.
   * Backend protégé par JWT — clé API jamais exposée côté front.
   * En cas d'échec API : renvoie {} pour échec silencieux côté UX (pas bloquant).
   */
  const handleFetchPlaceDetails = useCallback(async (placeId) => {
    try {
      const r = await apiGet(`/api/restaurants/place-details?placeId=${encodeURIComponent(placeId)}`);
      return {
        address: r?.address || '',
        phone:   r?.phone   || '',
        website: r?.website || '',
      };
    } catch {
      return { address: '', phone: '', website: '' };
    }
  }, []);

  if (loading) {
    return (
      <div style={{
        padding: 40, textAlign: 'center', color: 'var(--text2)', fontSize: 14,
      }}>
        🌿 Chargement de votre configuration…
      </div>
    );
  }

  return (
    <>
      <TabConfiguration
        initialData={initialData || {}}
        onComplete={handleComplete}
        onValidatePlaceId={handleValidatePlaceId}
        onFetchPlaceDetails={handleFetchPlaceDetails}
      />
      {/* Sprint 3 P0 — Zone de danger RGPD :
          • Article 15+20 : "Exporter mes données" → GET /api/account/export
          • Article 17     : "Supprimer mon compte"  → POST /api/account/delete
          Engagement légal : cette zone DOIT être ici (cf. politique de
          confidentialité §5 — "Configuration → Zone de danger"). */}
      <DangerZoneRGPD />
    </>
  );
}
